import { useState, useMemo } from "react";
import AdminLayout from "@/components/feature/AdminLayout";
import { useAdsDB } from "@/hooks/useAdsDB";
import {
  type AdPlacement,
  type AdType,
  type AdDevice,
  type AdPageType,
  type AdPosition,
} from "@/mocks/admin";

/* ─── Constants ─── */
const positionLabels: Record<AdPosition, string> = {
  header_banner: "Header Banner",
  top_leaderboard: "Top Leaderboard",
  sidebar_top: "Sidebar Rectangle",
  sidebar_large: "Sidebar Large",
  in_article_p2: "In-Article (Absatz 2)",
  in_article_p5: "In-Article (Absatz 5)",
  homepage_section: "Homepage Section",
  hero_grid_bottom: "Hero Grid Altı",
  category_page: "Category Page",
  article_bottom: "Article Bottom",
  footer_banner: "Footer Banner",
  mobile_top: "Mobile Top",
  mobile_in_article: "Mobile In-Article",
  mobile_between_cards: "Mobile Between Cards",
  mobile_sticky_bottom: "Mobile Sticky Bottom",
  mobile_above_authors: "Mobile über Autoren",
  mobile_after_hero: "Mobile nach Hero",
  mobile_between_sections: "Mobile zwischen Sektionen",
  mobile_between_cards_cat: "Mobile Kategorie Cards Arası",
  mobile_before_footer: "Mobile vor Footer",
  mobile_article_mid: "Mobile Artikel Mitte",
  mobile_between_cards_home: "Mobile Homepage Cards Arası",
  mobile_search_top: "Mobile Suche Oben",
  mobile_search_between: "Mobile Suche Zwischen",
  mobile_search_footer: "Mobile Suche Unten",
};

const adTypeLabels: Record<AdType, string> = {
  adsense: "Google AdSense",
  custom_html: "Custom HTML",
  image_banner: "Image Banner",
};

const deviceLabels: Record<AdDevice, string> = {
  desktop: "Desktop",
  tablet: "Tablet",
  mobile: "Mobile",
  all: "Alle Geräte",
};

const pageTypeLabels: Record<AdPageType, string> = {
  homepage: "Homepage",
  article: "Artikel",
  category: "Kategorie",
  search: "Suche",
  all: "Alle Seiten",
};

const adTypeColors: Record<AdType, string> = {
  adsense: "bg-emerald-100 text-emerald-700",
  custom_html: "bg-amber-100 text-amber-700",
  image_banner: "bg-sky-100 text-sky-700",
};

const deviceIcons: Record<AdDevice, string> = {
  desktop: "ri-computer-line",
  tablet: "ri-tablet-line",
  mobile: "ri-smartphone-line",
  all: "ri-layout-grid-line",
};

/* ─── Empty form ─── */
const emptyForm = (): Omit<AdPlacement, "id" | "impressions" | "clicks" | "createdAt" | "updatedAt"> => ({
  name: "",
  title: "",
  description: "",
  position: "sidebar_top",
  adType: "adsense",
  device: "desktop",
  pageType: "all",
  isActive: true,
  adsensePublisherId: "ca-pub-XXXXXXXXXX",
  adsenseSlotId: "",
  customHtml: "",
  imageUrl: "",
  imageAlt: "",
  linkUrl: "",
  width: 300,
  height: 250,
});

/* ─── Ad Preview ─── */
function AdPreview({ form }: { form: ReturnType<typeof emptyForm> }) {
  const maxW = Math.min(form.width, 600);
  const maxH = Math.min(form.height, 300);
  const ratio = form.height / form.width;
  const displayH = Math.min(maxH, maxW * ratio);

  return (
    <div className="border border-hj-gray-border rounded-lg overflow-hidden bg-stone-50">
      <div className="px-4 py-2 border-b border-hj-gray-border bg-white flex items-center gap-2">
        <i className="ri-eye-line text-hj-gray text-sm"></i>
        <span className="text-xs font-medium text-hj-gray">Vorschau ({form.width}×{form.height})</span>
      </div>
      <div className="p-4 flex items-center justify-center">
        <div
          className="relative border-2 border-dashed border-stone-300 rounded bg-stone-100 flex flex-col items-center justify-center gap-2"
          style={{ width: maxW, height: displayH, maxWidth: "100%" }}
        >
          <span className="text-[10px] font-medium text-stone-400 uppercase tracking-widest absolute top-1">
            Werbung
          </span>
          {form.adType === "image_banner" && form.imageUrl ? (
            <img
              src={form.imageUrl}
              alt={form.imageAlt || "Ad preview"}
              className="w-full h-full object-cover rounded"
            />
          ) : form.adType === "adsense" ? (
            <div className="flex flex-col items-center gap-1 mt-4">
              <i className="ri-google-fill text-xl text-stone-400"></i>
              <span className="text-xs text-stone-400 font-medium">Google AdSense</span>
              {form.adsensePublisherId && (
                <span className="text-xs text-stone-300 font-mono">{form.adsensePublisherId}</span>
              )}
              {form.adsenseSlotId && (
                <span className="text-xs text-stone-300">Slot: {form.adsenseSlotId}</span>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1 mt-4">
              <i className="ri-code-s-slash-line text-xl text-stone-400"></i>
              <span className="text-xs text-stone-400 font-medium">Custom HTML</span>
            </div>
          )}
          <span className="text-xs text-stone-300 absolute bottom-1">
            {form.width}×{form.height}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ─── Modal ─── */
interface ModalProps {
  ad: AdPlacement | null;
  onClose: () => void;
  onSave: (data: AdPlacement) => void;
}

function AdModal({ ad, onClose, onSave }: ModalProps) {
  const [form, setForm] = useState<Omit<AdPlacement, "id" | "impressions" | "clicks" | "createdAt" | "updatedAt">>(
    ad
      ? {
          name: ad.name,
          title: ad.title,
          description: ad.description || "",
          position: ad.position,
          adType: ad.adType,
          device: ad.device,
          pageType: ad.pageType,
          isActive: ad.isActive,
          adsensePublisherId: ad.adsensePublisherId || "ca-pub-XXXXXXXXXX",
          adsenseSlotId: ad.adsenseSlotId || "",
          customHtml: ad.customHtml || "",
          imageUrl: ad.imageUrl || "",
          imageAlt: ad.imageAlt || "",
          linkUrl: ad.linkUrl || "",
          width: ad.width,
          height: ad.height,
        }
      : emptyForm()
  );
  const [activeTab, setActiveTab] = useState<"general" | "code" | "preview">("general");

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    const now = new Date().toISOString();
    onSave({
      ...form,
      id: ad?.id || `ad-${Date.now()}`,
      impressions: ad?.impressions || 0,
      clicks: ad?.clicks || 0,
      createdAt: ad?.createdAt || now,
      updatedAt: now,
    });
  };

  const tabs = [
    { id: "general" as const, label: "Allgemein", icon: "ri-settings-3-line" },
    { id: "code" as const, label: "Ad-Code", icon: "ri-code-s-slash-line" },
    { id: "preview" as const, label: "Vorschau", icon: "ri-eye-line" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-hj-gray-border">
          <h2 className="text-lg font-bold text-hj-dark font-heading">
            {ad ? "Werbeplatzierung bearbeiten" : "Neue Werbeplatzierung"}
          </h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-hj-gray-light transition-colors cursor-pointer">
            <i className="ri-close-line text-hj-gray"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-hj-gray-border px-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === tab.id
                  ? "border-hj-red text-hj-red"
                  : "border-transparent text-hj-gray hover:text-hj-dark"
              }`}
            >
              <i className={tab.icon}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === "general" && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-hj-dark mb-1.5">Name *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    placeholder="z.B. Header Banner Desktop"
                    className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-hj-dark mb-1.5">Titel (intern)</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => set("title", e.target.value)}
                    placeholder="Interner Titel"
                    className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-hj-dark mb-1.5">Beschreibung</label>
                <input
                  type="text"
                  value={form.description || ""}
                  onChange={(e) => set("description", e.target.value)}
                  placeholder="Kurze Beschreibung dieser Platzierung"
                  className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-hj-dark mb-1.5">Position</label>
                  <select
                    value={form.position}
                    onChange={(e) => set("position", e.target.value as AdPosition)}
                    className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red bg-white"
                  >
                    {(Object.entries(positionLabels) as [AdPosition, string][]).map(([val, label]) => (
                      <option key={val} value={val}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-hj-dark mb-1.5">Ad-Typ</label>
                  <select
                    value={form.adType}
                    onChange={(e) => set("adType", e.target.value as AdType)}
                    className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red bg-white"
                  >
                    <option value="adsense">Google AdSense</option>
                    <option value="custom_html">Custom HTML</option>
                    <option value="image_banner">Image Banner</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-hj-dark mb-1.5">Gerät</label>
                  <select
                    value={form.device}
                    onChange={(e) => set("device", e.target.value as AdDevice)}
                    className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red bg-white"
                  >
                    <option value="all">Alle Geräte</option>
                    <option value="desktop">Desktop</option>
                    <option value="tablet">Tablet</option>
                    <option value="mobile">Mobile</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-hj-dark mb-1.5">Seitentyp</label>
                  <select
                    value={form.pageType}
                    onChange={(e) => set("pageType", e.target.value as AdPageType)}
                    className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red bg-white"
                  >
                    <option value="all">Alle Seiten</option>
                    <option value="homepage">Homepage</option>
                    <option value="article">Artikel</option>
                    <option value="category">Kategorie</option>
                    <option value="search">Suche</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-hj-dark mb-1.5">Breite (px)</label>
                  <input
                    type="number"
                    value={form.width}
                    onChange={(e) => set("width", Number(e.target.value))}
                    className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-hj-dark mb-1.5">Höhe (px)</label>
                  <input
                    type="number"
                    value={form.height}
                    onChange={(e) => set("height", Number(e.target.value))}
                    className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                  />
                </div>
              </div>

              {/* Quick size presets */}
              <div>
                <label className="block text-xs font-semibold text-hj-dark mb-2">Grössen-Vorlagen</label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: "970×250", w: 970, h: 250 },
                    { label: "728×90", w: 728, h: 90 },
                    { label: "300×250", w: 300, h: 250 },
                    { label: "300×600", w: 300, h: 600 },
                    { label: "320×100", w: 320, h: 100 },
                    { label: "320×50", w: 320, h: 50 },
                  ].map((preset) => (
                    <button
                      key={preset.label}
                      type="button"
                      onClick={() => { set("width", preset.w); set("height", preset.h); }}
                      className={`px-2.5 py-1 text-xs rounded-md border transition-colors cursor-pointer whitespace-nowrap ${
                        form.width === preset.w && form.height === preset.h
                          ? "bg-hj-red text-white border-hj-red"
                          : "border-hj-gray-border text-hj-gray hover:border-hj-red hover:text-hj-red"
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-hj-gray-light rounded-lg">
                <div>
                  <p className="text-sm font-medium text-hj-dark">Aktiv</p>
                  <p className="text-xs text-hj-gray">Werbeplatzierung aktivieren/deaktivieren</p>
                </div>
                <button
                  type="button"
                  onClick={() => set("isActive", !form.isActive)}
                  className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${form.isActive ? "bg-emerald-500" : "bg-stone-300"}`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${form.isActive ? "translate-x-5" : "translate-x-0.5"}`}></span>
                </button>
              </div>
            </div>
          )}

          {activeTab === "code" && (
            <div className="space-y-5">
              {form.adType === "adsense" && (
                <>
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start gap-3">
                    <i className="ri-google-fill text-emerald-600 mt-0.5"></i>
                    <div>
                      <p className="text-sm font-semibold text-emerald-800">Google AdSense</p>
                      <p className="text-xs text-emerald-700 mt-0.5">
                        Geben Sie Ihre Publisher-ID und Slot-ID ein. Der AdSense-Code wird automatisch generiert.
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-hj-dark mb-1.5">Publisher-ID</label>
                    <input
                      type="text"
                      value={form.adsensePublisherId || ""}
                      onChange={(e) => set("adsensePublisherId", e.target.value)}
                      placeholder="ca-pub-XXXXXXXXXX"
                      className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-hj-dark mb-1.5">Ad Slot ID</label>
                    <input
                      type="text"
                      value={form.adsenseSlotId || ""}
                      onChange={(e) => set("adsenseSlotId", e.target.value)}
                      placeholder="1234567890"
                      className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-hj-dark mb-1.5">Generierter Code</label>
                    <div className="bg-stone-900 rounded-lg p-4 font-mono text-xs text-emerald-400 overflow-x-auto">
                      <pre>{`<ins class="adsbygoogle"\n  style="display:block"\n  data-ad-client="${form.adsensePublisherId || "ca-pub-XXXXXXXXXX"}"\n  data-ad-slot="${form.adsenseSlotId || "XXXXXXXXXX"}"\n  data-ad-format="auto"\n  data-full-width-responsive="true">\n</ins>`}</pre>
                    </div>
                  </div>
                </>
              )}

              {form.adType === "custom_html" && (
                <>
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                    <i className="ri-code-s-slash-line text-amber-600 mt-0.5"></i>
                    <div>
                      <p className="text-sm font-semibold text-amber-800">Custom HTML</p>
                      <p className="text-xs text-amber-700 mt-0.5">
                        Fügen Sie beliebigen HTML-Code ein. Unterstützt auch JavaScript-Tags.
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-hj-dark mb-1.5">HTML-Code</label>
                    <textarea
                      value={form.customHtml || ""}
                      onChange={(e) => set("customHtml", e.target.value)}
                      rows={8}
                      placeholder="<div><!-- Ihr Werbecode hier --></div>"
                      className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red resize-none"
                    />
                  </div>
                </>
              )}

              {form.adType === "image_banner" && (
                <>
                  <div className="p-4 bg-sky-50 border border-sky-200 rounded-lg flex items-start gap-3">
                    <i className="ri-image-line text-sky-600 mt-0.5"></i>
                    <div>
                      <p className="text-sm font-semibold text-sky-800">Image Banner</p>
                      <p className="text-xs text-sky-700 mt-0.5">
                        Laden Sie ein Bild hoch und verlinken Sie es mit einer URL.
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-hj-dark mb-1.5">Bild-URL</label>
                    <input
                      type="url"
                      value={form.imageUrl || ""}
                      onChange={(e) => set("imageUrl", e.target.value)}
                      placeholder="https://example.com/banner.jpg"
                      className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-hj-dark mb-1.5">Alt-Text</label>
                    <input
                      type="text"
                      value={form.imageAlt || ""}
                      onChange={(e) => set("imageAlt", e.target.value)}
                      placeholder="Beschreibung des Banners"
                      className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-hj-dark mb-1.5">Ziel-URL (Link)</label>
                    <input
                      type="url"
                      value={form.linkUrl || ""}
                      onChange={(e) => set("linkUrl", e.target.value)}
                      placeholder="https://example.com/landing-page"
                      className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                    />
                  </div>
                  {form.imageUrl && (
                    <div>
                      <label className="block text-xs font-semibold text-hj-dark mb-1.5">Bildvorschau</label>
                      <div className="border border-hj-gray-border rounded-lg overflow-hidden h-32">
                        <img src={form.imageUrl} alt={form.imageAlt || "Banner"} className="w-full h-full object-cover" />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === "preview" && (
            <div className="space-y-4">
              <p className="text-sm text-hj-gray">
                So wird die Werbeplatzierung auf der Website aussehen:
              </p>
              <AdPreview form={form} />
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 bg-hj-gray-light rounded-lg">
                  <p className="text-xs text-hj-gray mb-1">Grösse</p>
                  <p className="font-semibold text-hj-dark">{form.width}×{form.height} px</p>
                </div>
                <div className="p-3 bg-hj-gray-light rounded-lg">
                  <p className="text-xs text-hj-gray mb-1">Typ</p>
                  <p className="font-semibold text-hj-dark">{adTypeLabels[form.adType]}</p>
                </div>
                <div className="p-3 bg-hj-gray-light rounded-lg">
                  <p className="text-xs text-hj-gray mb-1">Gerät</p>
                  <p className="font-semibold text-hj-dark">{deviceLabels[form.device]}</p>
                </div>
                <div className="p-3 bg-hj-gray-light rounded-lg">
                  <p className="text-xs text-hj-gray mb-1">Seite</p>
                  <p className="font-semibold text-hj-dark">{pageTypeLabels[form.pageType]}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-hj-gray-border bg-hj-gray-light/50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-hj-gray border border-hj-gray-border rounded-md hover:bg-hj-gray-light transition-colors cursor-pointer whitespace-nowrap"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            className="px-5 py-2 text-sm font-medium text-white bg-hj-red rounded-md hover:bg-hj-red-light transition-colors cursor-pointer whitespace-nowrap"
          >
            {ad ? "Speichern" : "Erstellen"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Delete Confirm ─── */
function DeleteConfirm({ ad, onConfirm, onCancel }: { ad: AdPlacement; onConfirm: () => void; onCancel: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel}></div>
      <div className="relative bg-white rounded-xl w-full max-w-sm p-6">
        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
          <i className="ri-delete-bin-line text-red-600 text-xl"></i>
        </div>
        <h3 className="text-lg font-bold text-hj-dark text-center mb-2">Platzierung löschen?</h3>
        <p className="text-sm text-hj-gray text-center mb-6">
          <strong>{ad.name}</strong> wird dauerhaft gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.
        </p>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 px-4 py-2 text-sm font-medium text-hj-gray border border-hj-gray-border rounded-md hover:bg-hj-gray-light transition-colors cursor-pointer">
            Abbrechen
          </button>
          <button onClick={onConfirm} className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors cursor-pointer">
            Löschen
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Stats Charts ─── */
function AdStatsCharts({ placements }: { placements: AdPlacement[] }) {
  const topImpressions = useMemo(() => {
    return [...placements]
      .filter((a) => a.impressions > 0)
      .sort((a, b) => b.impressions - a.impressions)
      .slice(0, 10);
  }, [placements]);

  const maxImpressions = useMemo(() => {
    return topImpressions.length > 0 ? Math.max(...topImpressions.map((a) => a.impressions)) : 1;
  }, [topImpressions]);

  const topCTR = useMemo(() => {
    return [...placements]
      .filter((a) => a.impressions > 0)
      .sort((a, b) => (b.clicks / b.impressions) - (a.clicks / a.impressions))
      .slice(0, 10);
  }, [placements]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
      {/* Impressions Bar Chart */}
      <div className="bg-white rounded-lg border border-hj-gray-border p-5">
        <h3 className="text-sm font-bold text-hj-dark mb-4 font-heading flex items-center gap-2">
          <i className="ri-bar-chart-box-line text-hj-red"></i>
          Impressions – Top 10
        </h3>
        <div className="space-y-2.5">
          {topImpressions.map((ad) => {
            const pct = (ad.impressions / maxImpressions) * 100;
            return (
              <div key={ad.id} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-hj-dark font-medium truncate max-w-[180px]">{ad.name}</span>
                  <span className="text-hj-muted ml-2 shrink-0">{ad.impressions.toLocaleString("de-CH")}</span>
                </div>
                <div className="h-5 bg-hj-gray-light rounded overflow-hidden">
                  <div
                    className="h-full bg-hj-red/80 rounded flex items-center justify-end px-1.5 transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  >
                    {pct > 25 && (
                      <span className="text-[10px] text-white font-medium">
                        {ad.impressions.toLocaleString("de-CH")}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {topImpressions.length === 0 && (
            <p className="text-xs text-hj-gray text-center py-4">Noch keine Impressions-Daten verfügbar.</p>
          )}
        </div>
      </div>

      {/* CTR Bar Chart */}
      <div className="bg-white rounded-lg border border-hj-gray-border p-5">
        <h3 className="text-sm font-bold text-hj-dark mb-4 font-heading flex items-center gap-2">
          <i className="ri-percent-line text-hj-red"></i>
          Click-Through Rate – Top 10
        </h3>
        <div className="space-y-2.5">
          {topCTR.map((ad) => {
            const ctr = (ad.clicks / ad.impressions) * 100;
            const maxCtr = Math.max(...topCTR.map((a) => (a.clicks / a.impressions) * 100), 0.5);
            const pct = Math.min((ctr / maxCtr) * 100, 100);
            return (
              <div key={ad.id} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-hj-dark font-medium truncate max-w-[180px]">{ad.name}</span>
                  <span className={`font-medium ml-2 shrink-0 ${ctr > 1 ? "text-emerald-600" : "text-hj-muted"}`}>
                    {ctr.toFixed(2)}%
                  </span>
                </div>
                <div className="h-5 bg-hj-gray-light rounded overflow-hidden">
                  <div
                    className={`h-full rounded flex items-center justify-end px-1.5 transition-all duration-500 ${ctr > 1 ? "bg-emerald-500/80" : "bg-hj-gray-border/80"}`}
                    style={{ width: `${pct}%` }}
                  >
                    {pct > 30 && (
                      <span className="text-[10px] text-white font-medium">
                        {ctr.toFixed(2)}%
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {topCTR.length === 0 && (
            <p className="text-xs text-hj-gray text-center py-4">Noch keine CTR-Daten verfügbar.</p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Main Page ─── */
export default function AdminAds() {
  const { placements, createAd, updateAd, deleteAd, refetch } = useAdsDB();
  const [search, setSearch] = useState("");
  const [filterDevice, setFilterDevice] = useState<AdDevice | "all">("all");
  const [filterPage, setFilterPage] = useState<AdPageType | "all">("all");
  const [filterType, setFilterType] = useState<AdType | "all">("all");
  const [filterStatus, setFilterStatus] = useState<"all" | "active" | "inactive">("all");
  const [editingAd, setEditingAd] = useState<AdPlacement | null | "new">(null);
  const [deletingAd, setDeletingAd] = useState<AdPlacement | null>(null);
  const [adsenseId, setAdsenseId] = useState("ca-pub-XXXXXXXXXX");
  const [editingAdsenseId, setEditingAdsenseId] = useState(false);

  const filtered = useMemo(() => {
    return placements.filter((ad) => {
      const matchSearch =
        !search ||
        ad.name.toLowerCase().includes(search.toLowerCase()) ||
        ad.title.toLowerCase().includes(search.toLowerCase()) ||
        (ad.description || "").toLowerCase().includes(search.toLowerCase());
      const matchDevice = filterDevice === "all" || ad.device === filterDevice || ad.device === "all";
      const matchPage = filterPage === "all" || ad.pageType === filterPage || ad.pageType === "all";
      const matchType = filterType === "all" || ad.adType === filterType;
      const matchStatus =
        filterStatus === "all" ||
        (filterStatus === "active" && ad.isActive) ||
        (filterStatus === "inactive" && !ad.isActive);
      return matchSearch && matchDevice && matchPage && matchType && matchStatus;
    });
  }, [placements, search, filterDevice, filterPage, filterType, filterStatus]);

  const stats = useMemo(() => {
    const total = placements.length;
    const active = placements.filter((a) => a.isActive).length;
    const totalImpressions = placements.reduce((s, a) => s + a.impressions, 0);
    const totalClicks = placements.reduce((s, a) => s + a.clicks, 0);
    const ctr = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : "0.00";
    const adsenseCount = placements.filter((a) => a.adType === "adsense").length;
    return { total, active, totalImpressions, totalClicks, ctr, adsenseCount };
  }, [placements]);

  const handleSave = (data: AdPlacement) => {
    if (editingAd === "new") {
      createAd(data).then(() => refetch());
    } else {
      updateAd(data.id, data).then(() => refetch());
    }
    setEditingAd(null);
  };

  const handleDelete = (ad: AdPlacement) => {
    deleteAd(ad.id).then(() => refetch());
    setDeletingAd(null);
  };

  const toggleActive = (id: string) => {
    const ad = placements.find((a) => a.id === id);
    if (!ad) return;
    updateAd(id, { isActive: !ad.isActive }).then(() => refetch());
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-hj-dark font-heading">Werbeverwaltung</h1>
            <p className="text-sm text-hj-gray mt-0.5">Google AdSense & Werbeplatzierungen verwalten</p>
          </div>
          <button
            onClick={() => setEditingAd("new")}
            className="flex items-center gap-2 px-4 py-2 bg-hj-red text-white text-sm font-medium rounded-md hover:bg-hj-red-light transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Neue Platzierung
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {[
            { label: "Gesamt", value: stats.total, icon: "ri-layout-grid-line", color: "text-hj-dark" },
            { label: "Aktiv", value: stats.active, icon: "ri-checkbox-circle-line", color: "text-emerald-600" },
            { label: "AdSense", value: stats.adsenseCount, icon: "ri-google-fill", color: "text-emerald-600" },
            { label: "Impressions", value: stats.totalImpressions.toLocaleString("de-CH"), icon: "ri-eye-line", color: "text-hj-dark" },
            { label: "Klicks", value: stats.totalClicks.toLocaleString("de-CH"), icon: "ri-cursor-line", color: "text-hj-dark" },
            { label: "CTR", value: `${stats.ctr}%`, icon: "ri-percent-line", color: "text-amber-600" },
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-lg border border-hj-gray-border p-4">
              <div className="flex items-center gap-2 mb-1">
                <i className={`${stat.icon} text-sm ${stat.color}`}></i>
                <span className="text-xs text-hj-gray">{stat.label}</span>
              </div>
              <p className={`text-xl font-bold ${stat.color} font-heading`}>{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Charts */}
        <AdStatsCharts placements={placements} />

        {/* AdSense Global Config */}
        <div className="bg-white rounded-lg border border-hj-gray-border p-5 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                <i className="ri-google-fill text-emerald-600 text-lg"></i>
              </div>
              <div>
                <h3 className="text-sm font-bold text-hj-dark">Google AdSense – Publisher-ID</h3>
                <p className="text-xs text-hj-gray mt-0.5">Globale Publisher-ID für alle AdSense-Platzierungen</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {editingAdsenseId ? (
                <>
                  <input
                    type="text"
                    value={adsenseId}
                    onChange={(e) => setAdsenseId(e.target.value)}
                    className="px-3 py-1.5 border border-hj-gray-border rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red w-52"
                  />
                  <button
                    onClick={() => setEditingAdsenseId(false)}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-hj-red rounded-md hover:bg-hj-red-light transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Speichern
                  </button>
                </>
              ) : (
                <>
                  <code className="text-sm font-mono text-hj-dark bg-hj-gray-light px-3 py-1.5 rounded-md">{adsenseId}</code>
                  <button
                    onClick={() => setEditingAdsenseId(true)}
                    className="px-3 py-1.5 text-xs font-medium text-hj-gray border border-hj-gray-border rounded-md hover:bg-hj-gray-light transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-edit-line mr-1"></i>
                    Bearbeiten
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-hj-gray-border p-4 mb-5">
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-hj-gray text-sm"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Platzierungen suchen..."
                className="w-full pl-9 pr-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as AdType | "all")}
              className="px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red bg-white"
            >
              <option value="all">Alle Typen</option>
              <option value="adsense">AdSense</option>
              <option value="custom_html">Custom HTML</option>
              <option value="image_banner">Image Banner</option>
            </select>
            <select
              value={filterDevice}
              onChange={(e) => setFilterDevice(e.target.value as AdDevice | "all")}
              className="px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red bg-white"
            >
              <option value="all">Alle Geräte</option>
              <option value="desktop">Desktop</option>
              <option value="tablet">Tablet</option>
              <option value="mobile">Mobile</option>
            </select>
            <select
              value={filterPage}
              onChange={(e) => setFilterPage(e.target.value as AdPageType | "all")}
              className="px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red bg-white"
            >
              <option value="all">Alle Seiten</option>
              <option value="homepage">Homepage</option>
              <option value="article">Artikel</option>
              <option value="category">Kategorie</option>
              <option value="search">Suche</option>
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as "all" | "active" | "inactive")}
              className="px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red bg-white"
            >
              <option value="all">Alle Status</option>
              <option value="active">Aktiv</option>
              <option value="inactive">Inaktiv</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-hj-gray-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hj-gray-border bg-hj-gray-light/50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-hj-gray uppercase tracking-wider">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-hj-gray uppercase tracking-wider">Position</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-hj-gray uppercase tracking-wider">Typ</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-hj-gray uppercase tracking-wider">Gerät</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-hj-gray uppercase tracking-wider">Seite</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-hj-gray uppercase tracking-wider">Grösse</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-hj-gray uppercase tracking-wider">Impressions</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-hj-gray uppercase tracking-wider">CTR</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-hj-gray uppercase tracking-wider">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-hj-gray uppercase tracking-wider">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-hj-gray-border">
                {filtered.map((ad) => {
                  const ctr = ad.impressions > 0 ? ((ad.clicks / ad.impressions) * 100).toFixed(2) : "0.00";
                  return (
                    <tr key={ad.id} className="hover:bg-hj-gray-light/30 transition-colors">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-semibold text-hj-dark">{ad.name}</p>
                          {ad.description && (
                            <p className="text-xs text-hj-gray mt-0.5 line-clamp-1">{ad.description}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-hj-gray bg-hj-gray-light px-2 py-0.5 rounded-full whitespace-nowrap">
                          {positionLabels[ad.position]}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${adTypeColors[ad.adType]}`}>
                          {adTypeLabels[ad.adType]}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <i className={`${deviceIcons[ad.device]} text-hj-gray text-sm`}></i>
                          <span className="text-xs text-hj-gray whitespace-nowrap">{deviceLabels[ad.device]}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-hj-gray whitespace-nowrap">{pageTypeLabels[ad.pageType]}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-mono text-hj-dark whitespace-nowrap">{ad.width}×{ad.height}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-xs text-hj-dark">{ad.impressions.toLocaleString("de-CH")}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`text-xs font-medium ${parseFloat(ctr) > 1 ? "text-emerald-600" : "text-hj-gray"}`}>
                          {ctr}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => toggleActive(ad.id)}
                          className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${ad.isActive ? "bg-emerald-500" : "bg-stone-300"}`}
                          title={ad.isActive ? "Deaktivieren" : "Aktivieren"}
                        >
                          <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${ad.isActive ? "translate-x-5" : "translate-x-0.5"}`}></span>
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setEditingAd(ad)}
                            className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-hj-gray-light transition-colors cursor-pointer"
                            title="Bearbeiten"
                          >
                            <i className="ri-edit-line text-hj-gray text-sm"></i>
                          </button>
                          <button
                            onClick={() => setDeletingAd(ad)}
                            className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-red-50 transition-colors cursor-pointer"
                            title="Löschen"
                          >
                            <i className="ri-delete-bin-line text-red-500 text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-advertisement-line text-4xl text-hj-gray-border mb-3"></i>
              <p className="text-hj-gray">Keine Platzierungen gefunden.</p>
            </div>
          )}
        </div>

        {/* Placement Map */}
        <div className="mt-8 bg-white rounded-lg border border-hj-gray-border p-6">
          <h2 className="text-base font-bold text-hj-dark mb-5 font-heading flex items-center gap-2">
            <i className="ri-map-2-line text-hj-red"></i>
            Platzierungsübersicht
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Desktop */}
            <div>
              <h3 className="text-sm font-semibold text-hj-dark mb-3 flex items-center gap-2">
                <i className="ri-computer-line text-hj-gray"></i>
                Desktop
              </h3>
              <div className="space-y-2">
                {placements.filter((a) => a.device === "desktop" || a.device === "all").map((ad) => (
                  <div key={ad.id} className="flex items-center justify-between p-2.5 rounded-lg bg-hj-gray-light/50 border border-hj-gray-border">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${ad.isActive ? "bg-emerald-500" : "bg-stone-300"}`}></div>
                      <span className="text-xs font-medium text-hj-dark">{ad.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-hj-gray">{ad.width}×{ad.height}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${adTypeColors[ad.adType]}`}>
                        {ad.adType === "adsense" ? "AS" : ad.adType === "custom_html" ? "HTML" : "IMG"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Mobile */}
            <div>
              <h3 className="text-sm font-semibold text-hj-dark mb-3 flex items-center gap-2">
                <i className="ri-smartphone-line text-hj-gray"></i>
                Mobile
              </h3>
              <div className="space-y-2">
                {placements.filter((a) => a.device === "mobile" || a.device === "all").map((ad) => (
                  <div key={ad.id} className="flex items-center justify-between p-2.5 rounded-lg bg-hj-gray-light/50 border border-hj-gray-border">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${ad.isActive ? "bg-emerald-500" : "bg-stone-300"}`}></div>
                      <span className="text-xs font-medium text-hj-dark">{ad.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-hj-gray">{ad.width}×{ad.height}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${adTypeColors[ad.adType]}`}>
                        {ad.adType === "adsense" ? "AS" : ad.adType === "custom_html" ? "HTML" : "IMG"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {editingAd !== null && (
        <AdModal
          ad={editingAd === "new" ? null : editingAd}
          onClose={() => setEditingAd(null)}
          onSave={handleSave}
        />
      )}
      {deletingAd && (
        <DeleteConfirm
          ad={deletingAd}
          onConfirm={() => handleDelete(deletingAd)}
          onCancel={() => setDeletingAd(null)}
        />
      )}
    </AdminLayout>
  );
}
